import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useCreateQuestionMutation } from "../../slices/questionApiSlice";
import { useGetQuestionBankSubjectsQuery } from "../../slices/questionBankSubjectApiSlice";
import { useGetChaptersQuery } from "../../slices/chapterApiSlice";

const CreateQuestionScreen = () => {
  const navigate = useNavigate();

  const [subject, setSubject] = useState("");
  const [chapter, setChapter] = useState("");

  const [questionText, setQuestionText] = useState("");

  const [options, setOptions] = useState([
    { text: "" },
    { text: "" },
    { text: "" },
    { text: "" },
  ]);

  const [correctAnswer, setCorrectAnswer] = useState("");
  const [explanation, setExplanation] = useState("");

  const [marks, setMarks] = useState(1);
  const [negativeMarks, setNegativeMarks] = useState(0);

  const [difficulty, setDifficulty] = useState("medium");
  const [questionType, setQuestionType] = useState("single");

  const [order, setOrder] = useState(1);
  const [isImage, setIsImage] = useState(false);

  const { data: subjects = [] } = useGetQuestionBankSubjectsQuery();

  const { data: chapters = [] } = useGetChaptersQuery(subject, {
    skip: !subject,
  });

  const [createQuestion, { isLoading }] = useCreateQuestionMutation();

  const optionChangeHandler = (index, value) => {
    const updatedOptions = [...options];

    updatedOptions[index].text = value;

    setOptions(updatedOptions);
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    if (!subject || !chapter || !questionText || !correctAnswer) {
      toast.error("Subject, chapter, question and correct answer are required");
      return;
    }

    const formattedOptions = options.map((item) => item.text.trim());

    if (formattedOptions.some((item) => !item)) {
      toast.error("All options are required");
      return;
    }

    try {
      await createQuestion({
        subject,
        chapter,
        questionText,

        options: formattedOptions,

        correctAnswer,

        explanation,

        marks: Number(marks),

        negativeMarks: Number(negativeMarks),

        difficulty,

        questionType,

        order: Number(order),

        isImage,
      }).unwrap();

      toast.success("Question created successfully");

      navigate("/questions");
    } catch (err) {
      const message =
        err?.data?.message || err?.error || "Failed to create question";

      switch (message) {
        case "Invalid question bank subject":
          toast.error("Invalid question bank subject");
          break;

        case "Invalid chapter":
          toast.error("Selected chapter does not belong to this subject");
          break;

        case "Question already exists in this chapter":
          toast.error("Question already exists in this chapter");
          break;

        default:
          toast.error(message);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <Link
          to="/questions"
          className="inline-flex items-center gap-2 mb-6 rounded-xl bg-white px-4 py-2 shadow hover:bg-blue-50 text-blue-600 transition"
        >
          ← Back to Questions
        </Link>

        <div className="rounded-3xl bg-white/80 backdrop-blur-lg shadow-2xl border border-white/30 p-6 md:p-10">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-800">
              Create Question
            </h1>

            <p className="mt-2 text-gray-500">
              Add a question to the question bank
            </p>
          </div>

          {isLoading && (
            <div className="mb-6 rounded-xl bg-blue-100 p-4 text-center text-blue-700">
              Creating question...
            </div>
          )}

          <form
            onSubmit={submitHandler}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {/* Subject */}

            <div>
              <label className="block mb-2 font-semibold text-gray-700">
                Subject *
              </label>

              <select
                value={subject}
                onChange={(e) => {
                  setSubject(e.target.value);
                  setChapter("");
                }}
                className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3"
              >
                <option value="">Select Subject</option>

                {subjects.map((item) => (
                  <option key={item._id} value={item._id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Chapter */}

            <div>
              <label className="block mb-2 font-semibold text-gray-700">
                Chapter *
              </label>

              <select
                value={chapter}
                onChange={(e) => setChapter(e.target.value)}
                disabled={!subject}
                className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 disabled:bg-gray-200"
              >
                <option value="">Select Chapter</option>

                {chapters.map((item) => (
                  <option key={item._id} value={item._id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Question */}

            <div className="md:col-span-2">
              <label className="block mb-2 font-semibold text-gray-700">
                Question *
              </label>

              <textarea
                rows="4"
                value={questionText}
                onChange={(e) => setQuestionText(e.target.value)}
                className="w-full resize-none rounded-xl border border-gray-300 bg-gray-50 px-4 py-3"
                placeholder="Enter question text"
              />
            </div>

            {/* Options */}

            <div className="md:col-span-2">
              <label className="block mb-3 font-semibold text-gray-700">
                Options *
              </label>

              <div className="space-y-4">
                {options.map((option, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <span className="w-8 font-bold text-blue-600">
                      {String.fromCharCode(65 + index)}
                    </span>

                    <input
                      type="text"
                      value={option.text}
                      onChange={(e) =>
                        optionChangeHandler(index, e.target.value)
                      }
                      className="flex-1 rounded-xl border border-gray-300 bg-gray-50 px-4 py-3"
                      placeholder={`Option ${index + 1}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Correct Answer */}

            <div>
              <label className="block mb-2 font-semibold text-gray-700">
                Correct Answer *
              </label>

              <input
                type="text"
                value={correctAnswer}
                onChange={(e) => setCorrectAnswer(e.target.value)}
                placeholder="Enter correct option text"
                className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3"
              />
            </div>

            {/* Question Type */}

            <div>
              <label className="block mb-2 font-semibold text-gray-700">
                Question Type
              </label>

              <select
                value={questionType}
                onChange={(e) => setQuestionType(e.target.value)}
                className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3"
              >
                <option value="single">Single Choice</option>

                <option value="multiple">Multiple Choice</option>
              </select>
            </div>

            {/* Difficulty */}

            <div>
              <label className="block mb-2 font-semibold text-gray-700">
                Difficulty
              </label>

              <select
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
                className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3"
              >
                <option value="easy">Easy</option>

                <option value="medium">Medium</option>

                <option value="hard">Hard</option>
              </select>
            </div>

            {/* Order */}

            <div>
              <label className="block mb-2 font-semibold text-gray-700">
                Order
              </label>

              <input
                type="number"
                min="1"
                value={order}
                onChange={(e) => setOrder(e.target.value)}
                className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3"
              />
            </div>

            {/* Marks */}

            <div>
              <label className="block mb-2 font-semibold text-gray-700">
                Marks
              </label>

              <input
                type="number"
                min="0"
                value={marks}
                onChange={(e) => setMarks(e.target.value)}
                className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3"
              />
            </div>

            {/* Negative Marks */}

            <div>
              <label className="block mb-2 font-semibold text-gray-700">
                Negative Marks
              </label>

              <input
                type="number"
                min="0"
                value={negativeMarks}
                onChange={(e) => setNegativeMarks(e.target.value)}
                className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3"
              />
            </div>

            {/* Image Question */}

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={isImage}
                onChange={(e) => setIsImage(e.target.checked)}
              />

              <label className="font-semibold text-gray-700">
                Question contains image
              </label>
            </div>

            {/* Explanation */}

            <div className="md:col-span-2">
              <label className="block mb-2 font-semibold text-gray-700">
                Explanation
              </label>

              <textarea
                rows="4"
                value={explanation}
                onChange={(e) => setExplanation(e.target.value)}
                className="w-full resize-none rounded-xl border border-gray-300 bg-gray-50 px-4 py-3"
                placeholder="Explain the answer"
              />
            </div>

            {/* Submit */}

            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 py-4 text-lg font-semibold text-white shadow-lg hover:from-blue-700 hover:to-indigo-700 disabled:opacity-60"
              >
                {isLoading ? "Creating..." : "Create Question"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateQuestionScreen;
